import * as Kalkulator from './Kalkulator';
import A from './Utilities/A-StudiestotteValg';
import B from './Utilities/B-SemestertypeValg';
import E from './Utilities/E-SkolepengerValg';
import D from './Utilities/D-BostedValg';
import { ValgState } from './Store/Valg/Types';
import Resultat from './Models/Resultat';
import { Utbetalingsplan } from './Models/Utbetalingsplan';
import { ResultatValg } from './Store/Resultat/Types';
import VeilederHounDto from './Models/VeilederHounDto';
import { Satser } from './Utilities/SatserApi';

export const StudiepoengHeltAr = 60;
export const StudiepoengEttSemester = 30;

export const getA = (state: ValgState): string | undefined => {
    return state.aStotte !== undefined ? state.aStotte.value : undefined;
};

export const getB = (state: ValgState): string | undefined => {
    return state.bSemester !== undefined ? state.bSemester.value : undefined;
};

export const getBx = (state: ValgState): string | undefined => {
    return state.bxSemester !== undefined ? state.bxSemester.value : undefined;
};

export const getD = (state: ValgState): string | undefined => {
    return state.dBosted !== undefined ? state.dBosted.value : undefined;
};

export const getE = (state: ValgState): string | undefined => {
    return state.eSkolepenger !== undefined ? state.eSkolepenger.value : undefined;
};

export const maksSkolepenger = (state: ValgState, resultat: ResultatValg, satser: Satser): number => {
    // finn max belop skolepenger
    const c = getStudiepoengForBeregning(state, resultat);
    return Math.ceil((satser.ST01_MaksSkolepengestottePerAr * c) / StudiepoengHeltAr);
};

export const skolepengerInfo = (state: ValgState): keyof VeilederHounDto => {
    if (getA(state) === A.a1_heltAr) {
        return 'maksSkolepengerFulltidHeltAr';
    } else if (getA(state) === A.a2_ettSemester) {
        return 'maksSkolepengerFulltidHalvtAr';
    } else if (getA(state) === A.a3_deltid && getB(state) === B.b1_heltAr) {
        return 'maksSkolepengerDeltidHeltAr';
    } else {
        return 'maksSkolepengerDeltidHalvtAr';
    }
};

export const beregnStipendOgLan = (state: ValgState, resultat: ResultatValg, satser: Satser): number => {
    const studiepoeng = getStudiepoengForBeregning(state, resultat);
    const antallMnd = getAntallMndForBeregning(state, resultat, studiepoeng);
    const skolepenger = hentSkolepengerForBeregning(state, resultat, satser);

    return Kalkulator.beregnStipendOgLan(antallMnd, skolepenger, studiepoeng, satser.ST01_MaksBasisstotteperManed);
};

export const beregnAndelStipendOgLan = (
    state: ValgState,
    resultat: ResultatValg,
    stipendOgLan: number,
    satser: Satser,
): Resultat => {
    const skolepenger = hentSkolepengerForBeregning(state, resultat, satser);
    const erBorteboer = getD(state) === D.d2_borte;

    return {
        stipendOgLan,
        stipend: Kalkulator.beregnStipend(erBorteboer, stipendOgLan, skolepenger),
        lan: Kalkulator.beregnLan(erBorteboer, stipendOgLan, skolepenger),
    };
};

export const utbetalingsplan = (
    state: ValgState,
    resultat: ResultatValg,
    stipendOgLan: number,
    satser: Satser,
): Utbetalingsplan => {
    const studiepoeng = getStudiepoengForBeregning(state, resultat);
    const skolepenger = hentSkolepengerForBeregning(state, resultat, satser);

    const antallMnd = getAntallMndForBeregning(state, resultat, studiepoeng);
    let hostperiode = 0;
    let varperiode = 0;

    if (antallMnd >= 10) {
        hostperiode = 5;
        varperiode = antallMnd === 11 ? 6 : 5;
    } else {
        const semesterType = getSemesterTypeForBeregning(state, resultat);
        hostperiode = semesterType === B.b2_host || semesterType === B.b4_host ? antallMnd : 0;
        varperiode = semesterType === B.b3_var || semesterType === B.b5_var ? antallMnd : 0;
    }

    return Kalkulator.beregnUtbetalingsplan(hostperiode, varperiode, stipendOgLan, skolepenger);
};

const hentSkolepengerForBeregning = (state: ValgState, resultat: ResultatValg, satser: Satser): number => {
    return getE(state) === E.e1_ja && state.fSpBelop
        ? Math.max(Math.min(state.fSpBelop, maksSkolepenger(state, resultat, satser)), 0)
        : 0;
};

const getStudiepoengForBeregning = (state: ValgState, resultat: ResultatValg): number => {
    return resultat.visBoksC
        ? Math.max(Math.min(state.cSp || 0, StudiepoengHeltAr), 0)
        : resultat.visBoksCx
        ? Math.max(Math.min(state.cxSp || 0, StudiepoengEttSemester), 0)
        : getA(state) === A.a1_heltAr
        ? StudiepoengHeltAr
        : StudiepoengEttSemester;
};

const getAntallMndForBeregning = (state: ValgState, resultat: ResultatValg, studiepoeng: number): number => {
    // Fulltid
    if (studiepoeng === StudiepoengHeltAr) {
        return 11;
    }

    const semesterType = getSemesterTypeForBeregning(state, resultat);
    // Deltid med heltidsbelastning
    if (erB3EllerB5(semesterType) && studiepoeng === StudiepoengEttSemester) {
        return 6;

        // Enkeltsemester og deltid
    } else {
        return B.antallMnd(semesterType);
    }
};

const erB3EllerB5 = (semesterType: string): boolean => {
    return semesterType === B.b5_var || semesterType === B.b3_var;
};

const getSemesterTypeForBeregning = (state: ValgState, resultat: ResultatValg): string => {
    const semestertypeRaw = resultat.visBoksB ? getB(state) : getBx(state);
    if (!semestertypeRaw) {
        throw new Error('Beregning feilet pga. B eller Bx ikke er satt');
    }
    return semestertypeRaw;
};
